/**
 * Слой связей: кривые между карточками, нарисованные поверх вёрстки.
 *
 * Карточки — обычный HTML, а не фигуры внутри SVG. Причина не в удобстве:
 * в SVG текст не переносится сам, и любое длинное имя площадки пришлось бы
 * резать руками. HTML же даёт настоящий текст — его выделяют, читает
 * скринридер, он масштабируется вместе со шрифтом браузера.
 *
 * Связи при этом рисуются: положение берётся из готовой вёрстки после
 * отрисовки, тем же приёмом, что подложка переключателя. Считать координаты
 * заранее нельзя — карточки разной высоты, и на узком экране колонки
 * перестраиваются.
 */
import { useLayoutEffect, useRef, useState } from 'react';

export interface Flow {
  from: string;
  to: string;
  /** Толщина по объёму: 0…1 от самого крупного потока. */
  weight: number;
  state: 'active' | 'stale' | 'broken' | 'planned' | 'none';
  label?: string;
}

interface Line {
  key: string;
  d: string;
  weight: number;
  state: Flow['state'];
  labelX: number;
  labelY: number;
  label?: string;
}

export function FlowCanvas({
  flows,
  containerRef,
  deps,
}: {
  flows: Flow[];
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Что означает «пересчитать»: данные, от которых зависит вёрстка. */
  deps: string;
}): React.JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const host = containerRef.current;
    if (!host) return;

    const measure = (): void => {
      const box = host.getBoundingClientRect();
      setSize({ w: box.width, h: box.height });

      const out: Line[] = [];
      for (const flow of flows) {
        const a = host.querySelector<HTMLElement>(`[data-node="${CSS.escape(flow.from)}"]`);
        const b = host.querySelector<HTMLElement>(`[data-node="${CSS.escape(flow.to)}"]`);
        if (!a || !b) continue;

        const ra = a.getBoundingClientRect();
        const rb = b.getBoundingClientRect();

        // Точки выхода — середины боковых граней, а не центры карточек:
        // линия из центра выглядит воткнутой в текст.
        const x1 = ra.right - box.left;
        const y1 = ra.top + ra.height / 2 - box.top;
        const x2 = rb.left - box.left;
        const y2 = rb.top + rb.height / 2 - box.top;

        // Кубическая кривая с горизонтальными касательными: провода выходят
        // из карточек вбок и входят вбок, без изломов у краёв.
        const bend = Math.max(28, (x2 - x1) * 0.45);
        out.push({
          key: `${flow.from}→${flow.to}`,
          d: `M ${x1} ${y1} C ${x1 + bend} ${y1}, ${x2 - bend} ${y2}, ${x2} ${y2}`,
          weight: flow.weight,
          state: flow.state,
          labelX: (x1 + x2) / 2,
          labelY: (y1 + y2) / 2 - 6,
          ...(flow.label ? { label: flow.label } : {}),
        });
      }
      setLines(out);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    for (const node of host.querySelectorAll('[data-node]')) observer.observe(node);
    return () => observer.disconnect();
  }, [deps, flows.length]);

  return (
    <svg
      ref={svgRef}
      className="flow"
      width={size.w}
      height={size.h}
      viewBox={`0 0 ${size.w || 1} ${size.h || 1}`}
      aria-hidden="true"
      focusable="false"
    >
      {lines.map((l) => (
        <g key={l.key} className={`flow__g flow__g--${l.state}`}>
          {/* Подложка шире самой линии: на тёмном фоне тонкая кривая тонет. */}
          <path className="flow__halo" d={l.d} strokeWidth={2 + l.weight * 14} />
          <path className="flow__line" d={l.d} strokeWidth={1 + l.weight * 8} />
          {/* Бегущий пунктир — только у живых потоков. У сломанного движение
              читалось бы как «всё идёт», а оно как раз не идёт. */}
          {l.state === 'active' && (
            <path className="flow__spark" d={l.d} strokeWidth={1 + l.weight * 8} />
          )}
        </g>
      ))}
    </svg>
  );
}
